/* @refresh reset */

import { useEffect, useRef, useState } from "react"

export const CHUNK_ROWS = 400
export const SLICE_MS = 8

export type ChunkedWorkOptions<Item, Result> = {
  chunkSize?: number
  createAcc: () => Result
  processChunk: (slice: readonly Item[], acc: Result) => void
  finalize: (acc: Result) => Result
  empty: Result
  workKey?: string | number
}

/**
 * This hook walks `source` in time-budgeted turns so the page still receives clicks.
 * The first `requestAnimationFrame` lets empty chrome commit. Each later turn takes `CHUNK_ROWS` bites until `SLICE_MS` of this frame is used, then yields.
 * `result` stays the last finished value (or `empty`) until the last turn. The table does not grow mid-walk.
 * The fetch flag stays the query's `isLoading`. Callers should pass `isLoading || isSlicing` into `useSkeletonPhases` so the content area stays loading until this walk publishes rows.
 */
export function useChunkedWork<Item, Result>(source: readonly Item[], options: ChunkedWorkOptions<Item, Result>): { result: Result; isSlicing: boolean } {
  const { chunkSize = CHUNK_ROWS, createAcc, processChunk, finalize, empty, workKey } = options
  const processChunkRef = useRef(processChunk)
  const finalizeRef = useRef(finalize)
  const createAccRef = useRef(createAcc)
  processChunkRef.current = processChunk
  finalizeRef.current = finalize
  createAccRef.current = createAcc

  const [result, setResult] = useState(empty)
  const finishedRef = useRef<{ source: readonly Item[]; workKey: string | number | undefined } | null>(null)
  const sourceRef = useRef(source)
  sourceRef.current = source
  // If the source is empty, there is nothing to walk. This hook treats that walk as already finished so `isSlicing` is false.
  if (source.length === 0) {
    finishedRef.current = { source, workKey }
  }
  // `isSlicing` is true until this `source` and `workKey` have published a `result`. Callers should OR this flag into `loading`.
  const isSlicing = source.length > 0 && (finishedRef.current?.source !== source || finishedRef.current.workKey !== workKey)

  useEffect(() => {
    if (source.length === 0) {
      setResult(empty)
      return
    }

    const acc = createAccRef.current()
    let index = 0
    let frame = 0
    const step = () => {
      // A newer walk now owns `source`, so this callback returns. The previous effect's cleanup cancelled the leftover animation frame.
      if (sourceRef.current !== source) {
        return
      }
      const frameStarted = performance.now()
      // Each turn keeps taking bites until this frame's CPU budget is used.
      while (index < source.length && performance.now() - frameStarted < SLICE_MS) {
        const end = Math.min(index + chunkSize, source.length)
        processChunkRef.current(source.slice(index, end), acc)
        index = end
      }
      if (index < source.length) {
        // This turn yields the rest of the frame so the browser can paint chrome and take hamburger clicks, then the next turn runs.
        frame = window.requestAnimationFrame(step)
        return
      }
      if (sourceRef.current !== source) {
        return
      }
      finishedRef.current = { source, workKey }
      // Only the last turn sorts, stamps, and publishes. The table jumps to the finished array here.
      setResult(finalizeRef.current(acc))
    }
    // This effect does not walk on this commit. Empty chrome and the showNothing timeout must be able to paint first.
    frame = window.requestAnimationFrame(step)
    return () => {
      if (frame) {
        window.cancelAnimationFrame(frame)
      }
    }
  }, [chunkSize, empty, source, workKey])

  return { result, isSlicing }
}
